import { memo, useState } from "react";

import { Flag01 } from "@hl/base-components/lib/assets/icons/HDS";
import { BrandJustLogo } from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  PriceType,
  UnlockGateResult,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import { envConfig } from "@hl/shared-features/lib/config";
import { GateConditionsResult } from "@hl/shared-features/lib/features/gate/GateConditionsResult";
import {
  Anchor,
  Box,
  Center,
  Container,
  Flex,
  Group,
  MantineTheme,
  Space,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { MintCard } from "../MintVector/MintCard";
import MintSaleAccordion from "../MintVector/MintSaleAccordion";
import { MintDetails } from "../components/DetailSection/MintDetails";
import { MintHero } from "../components/MintHero";
import { TokenDescription } from "../components/TokenDescription";
import { MintVector, Collection } from "../index";
import MarketplacePreviewGallery from "../marketplace/MarketplacePreviewGallery";
import {
  GetSalePageMintVectorsDocument,
  useSeriesTokensQuery,
} from "../queries.graphql.generated";
import { reportCollectionMailtoUri } from "../utils/uris";

import Shloms404Carousel from "./Shloms404Carousel";

type Shloms404MintPageProps = {
  collectionId: string;
  mintVectorsLoading: boolean;
  auctionsLoading: boolean;
  userGateAccess?: UnlockGateResult;
  mintVector?: MintVector;
  isTablet: boolean;
  collection: Collection;
  creatorAddresses?: { address: string }[];
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  classes: { layout: any; leftColumn: any };
  isMarketplaceEnabledForCollectionChain: boolean;
  isMarketplaceNoticeShown: boolean;
  theme: MantineTheme;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonRef: (element: any) => void;
  onMintClick: (
    chainId: number,
    tokenIdNum?: number | null | undefined,
    imageUrl?: string | undefined
  ) => Promise<void>;
  refetch: () => Promise<void>;
  showGatePrereqs?: () => void;
};

const Shloms404MintPage = ({
  collectionId,
  mintVectorsLoading,
  auctionsLoading,
  userGateAccess,
  mintVector,
  isTablet,
  collection,
  creatorAddresses,
  description,
  classes,
  isMarketplaceEnabledForCollectionChain,
  isMarketplaceNoticeShown,
  theme,
  buttonRef,
  onMintClick,
  refetch,
  showGatePrereqs,
}: Shloms404MintPageProps) => {
  const [selectedTokenIndex, setSelectedTokenIndex] = useState<number>(0);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);

  const {
    data: tokensData,
    fetchMore,
    loading,
  } = useSeriesTokensQuery({
    variables: {
      collectionId: collectionId!,
      limit: isMobile ? 5 : 7,
      cursor: null,
      minted: null,
    },
  });

  const seriesTokens = tokensData?.getPublicCollection?.seriesTokens;
  const tokens = seriesTokens?.edges ?? [];
  const nextCursor = seriesTokens?.pageInfo?.endCursor;

  return (
    <>
      <MintHero hideMediaHeader={true} />
      <Container size="md" mt="sm" mb="md">
        <Stack align="center" spacing={16} w="100%">
          <Text size="sm" align="center" color={TEXT_COLOR.PLACEHOLDER}>
            This project will be released as a Dutch Auction with a rebate; all
            bidders will pay the same price as the 256th bid and will receive
            token IDs sequentially (the first bidder will get token ID 1 and so
            on).
          </Text>
          {description && <TokenDescription description={description} />}
        </Stack>
      </Container>
      <Space h={30} />
      <Shloms404Carousel
        selectedTokenIndex={selectedTokenIndex}
        setSelectedTokenIndex={setSelectedTokenIndex}
        tokens={tokens}
        loading={loading}
        nextCursor={nextCursor}
        fetchMore={fetchMore}
      />
      <Space h={40} />
      <Container
        size="md"
        mb={mintVector?.mintVectorStats?.sold ?? 0 > 0 ? 120 : 140}
      >
        <Flex className={classes.layout}>
          <Box>
            <>
              {!mintVectorsLoading &&
                !auctionsLoading &&
                userGateAccess &&
                showGatePrereqs && (
                  <>
                    <Space h="xs" />
                    <GateConditionsResult
                      creatorAddress={
                        creatorAddresses
                          ? creatorAddresses[0].address
                          : undefined
                      }
                      creatorEns={collection?.creatorEns}
                      refetchOnRequirementChange={[
                        GetSalePageMintVectorsDocument,
                      ]}
                      handleShowModal={showGatePrereqs}
                      unlockGateResult={userGateAccess}
                      showFirstN={4}
                      showConnectWallet={false}
                      entity={"mint"}
                      multipleMintVectorEnableMintPage={
                        collection?.flagVariations
                          .multipleMintVectorsEnableMintPage
                      }
                    />
                    <Space h="xl" />
                  </>
                )}
              {mintVector && (
                <Stack
                  style={{ width: isTablet ? "100%" : 360 }}
                  mb={isMobile || isTablet ? 30 : "xs"}
                >
                  {collection?.flagVariations
                    .multipleMintVectorsEnableMintPage && (
                    <MintSaleAccordion
                      handleShowModal={showGatePrereqs}
                      unlockGateResult={userGateAccess}
                    />
                  )}
                  {(!isMarketplaceNoticeShown ||
                    mintVector.priceType === PriceType.DutchAuction) && (
                    <MintCard
                      ref={buttonRef}
                      onMintClick={onMintClick}
                      onTimesUp={refetch}
                      hideRebateMessage={true}
                    />
                  )}
                </Stack>
              )}
              {mintVector &&
                collection &&
                isMarketplaceEnabledForCollectionChain && (
                  <MarketplacePreviewGallery collectionId={collectionId} />
                )}
            </>
          </Box>
          <Box className={classes.leftColumn}>
            <MintDetails
              collectionId={collectionId}
              selectedSeriesToken={tokens[selectedTokenIndex]}
            />
          </Box>
        </Flex>
      </Container>
      <Center pb={{ base: 60, xl: 60 }} p={20}>
        <UnstyledButton
          component="a"
          href={collection ? reportCollectionMailtoUri(collection) : ""}
          target="_blank"
          rel="noreferrer"
        >
          <Group spacing={6}>
            <Flag01 width={14} />
            <Text size="sm">Report this collection</Text>
          </Group>
        </UnstyledButton>
      </Center>
      <Flex justify="center">
        <Anchor href={envConfig.home.baseUrl} target="_blank" rel="noreferrer">
          <BrandJustLogo
            height={14}
            width={20}
            color={theme.colors.primaryText[0]}
          />
        </Anchor>
      </Flex>
      <Space h={20} />
    </>
  );
};

export default memo(Shloms404MintPage);
